<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="Recurring meetings on autopilot"
            subtitle="Keep weekly and bi-weekly client sessions consistent without manual rebooking."
            buttonLabel="Launch recurring sessions"
            :buttonUrl="heroRegisterUrl"
        />

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div class="order-1 lg:order-1">
                        <FeaturePageSectionHeader
                            title="Designed for long-term client cadence"
                            subtitle="Set recurring schedules once, lock preferred time slots, and let reminders keep attendance high."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <div class="mt-7 flex justify-start">
                            <FeaturePagePrimaryButton
                                :href="ctaRegisterUrl"
                                label="Launch recurring sessions"
                            />
                        </div>
                    </div>

                    <div class="order-2 lg:order-2">
                        <FeaturePageMockPanel
                            title="Weekly Coaching Plan"
                            badgeText="Every Tuesday"
                        >
                            <FeaturePageInfoCard
                                title="Session cadence"
                                description="Weekly • 45 min • 6 sessions"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />

                            <FeaturePageInfoCard
                                title="Next meeting"
                                description="Tue • 11:00 • Google Meet"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />

                            <FeaturePageInfoCard
                                title="Reminder status"
                                description="Automatic reminders active for each session."
                                containerClass="rounded-xl border border-slate-200 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Recurring meeting capabilities"
                    subtitle="Standardize long-term client cadence with scheduling, reminders, and payment in one setup."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="recurringFeature in recurringFeatures"
                        :key="recurringFeature.title"
                        :title="recurringFeature.title"
                        :description="recurringFeature.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    />
                </div>
            </div>
        </section>

        <section class="bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Sample use cases"
                    subtitle="Recurring meetings are ideal when clients need a predictable rhythm over weeks or months."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="sampleUseCase in sampleUseCases"
                        :key="sampleUseCase.title"
                        :title="sampleUseCase.title"
                        :description="sampleUseCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface RecurringFeature {
        title: string
        description: string
    }

    interface SampleUseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.RecurringMeetingsHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.RecurringMeetingsCta,
    )

    const recurringFeatures: RecurringFeature[] = [
        {
            title: 'Set cadence once',
            description:
                'Configure weekly, bi-weekly, or monthly recurring schedules in minutes.',
        },
        {
            title: 'Lock preferred time slots',
            description:
                'Keep the same recurring slot so clients build consistent habits.',
        },
        {
            title: 'Automatic reminders',
            description:
                'Reduce no-shows with reminder emails before each recurring session.',
        },
        {
            title: 'Collect payments upfront',
            description:
                'Charge clients during booking so recurring sessions are confirmed only after payment.',
        },
    ]

    const sampleUseCases: SampleUseCase[] = [
        {
            title: 'Coaching retainers',
            description:
                'Book weekly accountability sessions with the same client slot every week.',
        },
        {
            title: 'Advisory retainers',
            description:
                'Maintain bi-weekly sessions while keeping reminders and meeting rhythm consistent for clients.',
        },
        {
            title: 'Mentorship programs',
            description:
                'Run recurring check-ins for founders, operators, or independent professionals who need ongoing support.',
        },
    ]
</script>
